import { Paper, Tooltip, useTheme } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useEffect, useState } from "react";
import { IconAndLabelColumnHeader } from "../../../utils/dataGrid";
import BuildIcon from "@mui/icons-material/Build";
import { useApi } from "../../../utils/Api";
import Preloader from "../../Preloader";
import Translate from "../../../utils/Translate";
import DotMenu from "../../DotMenu";
import { CustomNoRowsOverlay } from "../../../utils/StyledGridOverlay";
import { dataGridLang } from "../../MemToolBar";
import AddIcon from "@mui/icons-material/Add";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SpeedMenu from "../../SpeedMenu";
import { hasAccess, accessKeys } from "../../../utils/userAccess";
import YesOrNoDialog from "../../YesOrNoDialog";
import { useCallback } from "react";
import AddOrEditSkillGroupDialog from "./AddOrEditSkillGroupDialog";
import { useMemo } from "react";
import SkillGroupSkillsDialog from "./SkillGroupSkillsDialog";
import BlockIcon  from "@mui/icons-material/Block";

const getSpeedMenu = (props) => {
  const menudata = [
    {
      icon: <AddIcon />,
      text: Translate.get("AddCourseFamily"),
      route: false,
      // dialogHeader: Translate.get("CreateCourseTemplate"),
      // customClass: "bigCourseDialog",
      component: <AddOrEditSkillGroupDialog />,
    },
  ];
  return menudata;
};

 const showAddForActorPermits = (skillGroup) => hasAccess(accessKeys.admin)
  && hasAccess(accessKeys.actorPermit) 
  && !skillGroup.actorPermitSkillGroup
  && skillGroup.skills.some(s => !s.isTheory);

  const showRemoveForActorPermits = (skillGroup) =>
  hasAccess(accessKeys.admin)
  && skillGroup.actorPermitSkillGroup;


export default function SkillGroupSettings(props) {
  const localizedTextsMap = dataGridLang();
  const [isLoading, setIsLoading] = useState(true);
  const [skillGroups, setSkillGroups] = useState([]);
  const [deleteSkillGroupId, setDeleteSkillGroupId] = useState(null);
  const api = useApi();
  const thm = useTheme();

  const loadSkillGroups = useCallback(async () => {
    const skillGroups = await api.fetch(`${import.meta.env.VITE_MAIN_URL}skillgroups/names`, false, "GET");
    setSkillGroups(skillGroups);
    setIsLoading(false);
  }, [api]);

  useEffect(() => {
    loadSkillGroups();
  }, [loadSkillGroups]);

  const getDotMenuActions = useCallback((skillGroup) => {
    const menudata = [
      {
        icon: <EditIcon />,
        dialogHeader: Translate.get("ChangeSkillGroupName"),
        text: Translate.get("ChangeSkillGroupName"),
        component: <AddOrEditSkillGroupDialog skillGroupId={skillGroup.skillGroupId} />,
        customClass: "mediumCourseDialog",
      },
      {
        icon: <ReceiptLongIcon />,
        text: Translate.get("ActivatePermit"),
        disabled: !showAddForActorPermits(skillGroup),
        onClick: () => toggleSkillGroupUsableForPermits(skillGroup.skillGroupId, true)
      },
      {
        icon: <BlockIcon />,
        text: Translate.get("InactivateSkillGroupForPemits"),
        disabled: !showRemoveForActorPermits(skillGroup),
        onClick: () => toggleSkillGroupUsableForPermits(skillGroup.skillGroupId, false)
      },
      {
        icon: <BuildIcon />,
        text: Translate.get("EditSkills"),
        // onClick: () => setEditSkillGroupId(skillGroupId),
        component: <SkillGroupSkillsDialog skillGroupId={skillGroup.skillGroupId} />,
      },
      {
        icon: <DeleteIcon />,
        text: Translate.get("RemoveCourseFamily"),
        onClick: () => setDeleteSkillGroupId(skillGroup.skillGroupId),
      },
    ];
    return menudata;
  }, []);

  async function toggleSkillGroupUsableForPermits(skillGroupId, useForPermits) {
    try {
      setIsLoading(true);
      const skillGroup = await api.fetch(`${import.meta.env.VITE_MAIN_URL}skillgroups/1/${skillGroupId}`, false, "GET");
      skillGroup.actorPermitSkillGroup = useForPermits;
      const response = await api.fetch(`${import.meta.env.VITE_MAIN_URL}skillgroups`, skillGroup, "POST");
      if(response.isSuccessful) {
        await loadSkillGroups();
      }
    } finally {
      setIsLoading(false);
    }
  }
 
  async function handleDeleteSkillGroup(skillGroupId) {
    const response = await api.fetch(`${import.meta.env.VITE_MAIN_URL}skillgroups/${skillGroupId}`, true, "DELETE");
    if (response.isSuccessful) {
      setDeleteSkillGroupId(null);
      setIsLoading(true);
      await loadSkillGroups();
    }
  }

  const columns = useMemo(
    () => [
      {
        field: "description",
        flex: 1,
        disableColumnMenu: true,
        headerClassName: "hideSeparator",
        headerName: Translate.get("CompetenceGroup"),
        renderHeader: () => <IconAndLabelColumnHeader icon={BuildIcon} label={Translate.get("CompetenceGroup")} />,
        sortComparator: (v1, v2) => v1.localeCompare(v2, "sv"),
        renderCell: (params) => <div style={{alignItems: "center", gap: ".62rem", display:"flex"}}> {/*extra styling för att få ikonen att centrera vertikalt i förhållande till texten */}
          {params.row.description} 
          {params.row.actorPermitSkillGroup ? <Tooltip title={Translate.get("SkillCanBeUsedInActorPermit")}><ReceiptLongIcon htmlColor={thm.palette.text.secondary} /></Tooltip> : null}
        </div>
      },
      {
        field: "dotMenu",
        flex: 0.3,
        headerName: "",
        sortable: false,
        disableColumnMenu: true,
        headerClassName: "hideSeparator",
        align: "right",
        renderCell: (params) => <DotMenu content={getDotMenuActions(params.row)} horizontal />,
      },
    ],
    [getDotMenuActions]
  );

  const [paginationModel, setPaginationModel] = useState({
    pageSize: 100,
    page: 0,
  });

  return (
    <>
      {isLoading && <Preloader />}
      {!isLoading && (
        <>
          <YesOrNoDialog
            open={!!deleteSkillGroupId}
            title={Translate.get("RemoveCourseFamily")}
            text={Translate.get("SureRemoveSkillGroup")}
            onYes={async () => await handleDeleteSkillGroup(deleteSkillGroupId)}
            onNo={() => setDeleteSkillGroupId(null)}
          />
          {hasAccess(accessKeys.advancedPackage) && <SpeedMenu content={getSpeedMenu()} />}

          <Paper>
            <DataGrid
              disableRowSelectionOnClick={true}
              pagination={true}
              pageSizeOptions={[10, 20, 30, 100]}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              autoHeight={true}
              localeText={localizedTextsMap}
              initialState={{
                sorting: {
                  sortModel: [{ field: "description", sort: "asc" }],
                },
              }}
              density="comfortable"
              columns={columns}
              rows={skillGroups}
              getRowId={(row) => row.skillGroupId}
              slots={{
                noRowsOverlay: CustomNoRowsOverlay(Translate.get("EmptyHere")),
              }}
            />
          </Paper>
        </>
      )}
    </>
  );
}
